import React, { useCallback } from 'react';
import { Button, Icon, Typography } from '@material-ui/core';
import useStyles from 'app/cluster-styles/useStyles';
import { IMAGE_TYPE } from '../../util/enums';
import SectionCard from './SectionCard';
import NodeGroupCard from './NodeGroupCard';

/**
 * Generic compute nodes section.
 * Owns the SectionCard, Add/Remove group CRUD, and NodeGroupCard mapping.
 * Provider-specific fields are rendered via the `renderGroupFields` prop.
 *
 * @param {object}   form                - flat form state
 * @param {function} setField            - form field setter
 * @param {boolean}  disabled            - disable all inputs
 * @param {number}   maxGroups           - max number of groups allowed (default 10)
 * @param {object}   newGroupTemplate    - template for new groups (merged with defaults)
 * @param {function} renderGroupFields   - (group, index, { updateGroup, groups }) => JSX
 */
export default function ComputeNodesSection({
    form, setField, disabled, maxGroups = 10,
    newGroupTemplate, renderGroupFields,
}) {
    const classes = useStyles();
    const groups = form.nodeGroups || [];

    const addGroup = useCallback(() => {
        if (groups.length >= maxGroups) return;
        const template = {
            name: `compute-${groups.length + 1}`,
            group_node_prefix_label: `Node Group ${groups.length + 1}`,
            count: 1,
            instance_id: '',
            image_type: IMAGE_TYPE.HEIDI_STACK,
            ...newGroupTemplate,
        };
        setField('nodeGroups', [...groups, template]);
    }, [groups, maxGroups, newGroupTemplate, setField]);

    // Head-node-only clusters are valid, so a group can always be removed - even
    // the last one. The submit path re-injects the required count:0 placeholder.
    const removeGroup = useCallback((index) => {
        setField('nodeGroups', groups.filter((_, i) => i !== index));
    }, [groups, setField]);

    const updateGroup = useCallback((index, field, value) => {
        const updated = [...groups];
        updated[index] = { ...updated[index], [field]: value };
        setField('nodeGroups', updated);
    }, [groups, setField]);

    return (
        <SectionCard
            icon="device_hub"
            title="Compute Nodes"
            action={
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<Icon>add</Icon>}
                    onClick={addGroup}
                    disabled={groups.length >= maxGroups || disabled}
                    className={classes.grayButton}
                >
                    Add Node Group
                </Button>
            }
        >
            {groups.length === 0 && (
                <Typography variant="body2" color="textSecondary" style={{ padding: '4px 2px' }}>
                    No compute nodes - this is a head-node-only cluster. Click "Add Node Group" to add compute nodes.
                </Typography>
            )}
            {groups.map((group, index) => (
                <NodeGroupCard
                    key={`node-group-${index}`}
                    index={index}
                    label={group.group_node_prefix_label || `Node Group ${index + 1}`}
                    onRemove={() => removeGroup(index)}
                    canRemove
                >
                    {renderGroupFields(group, index, { updateGroup, groups })}
                </NodeGroupCard>
            ))}
        </SectionCard>
    );
}
